import React, { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../utils/api';
import { addLeafletCompassControl } from '../utils/leafletCompassControl';
import { CASE_LOCATION_DEFAULT_ZOOM, INTA_CORDOBA_CENTER, INTA_CORDOBA_LABEL } from '../utils/mapDefaults';

const DEFAULT_CENTER = INTA_CORDOBA_CENTER;
const DEFAULT_ZOOM = CASE_LOCATION_DEFAULT_ZOOM;
const DETAIL_ZOOM = 13;
const SEARCH_ZOOM = 11;
const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const OVERPASS_BASE_URL = 'https://overpass-api.de/api/interpreter';
const ROUTE_LABEL_PATTERN = /\b(?:ruta(?:\s+(?:nacional|provincial))?|rn|rp)\b/i;

const parseCoordinate = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeWhitespace = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const decodeSearchText = (value) => {
  try {
    return decodeURIComponent(String(value || '').replace(/\+/g, ' ')).trim();
  } catch (error) {
    return String(value || '').replace(/\+/g, ' ').trim();
  }
};

const formatRouteLabel = (value = '') => {
  const normalized = normalizeWhitespace(value)
    .replace(/\bRN\b/gi, 'Ruta Nacional')
    .replace(/\bRP\b/gi, 'Ruta Provincial');
  const routeMatch = normalized.match(/^ruta\s+nacional\s+(.+)$/i);
  if (routeMatch?.[1]) {
    return `Ruta Nacional ${normalizeWhitespace(routeMatch[1])}`;
  }

  const provincialMatch = normalized.match(/^ruta\s+provincial\s+(.+)$/i);
  if (provincialMatch?.[1]) {
    return `Ruta Provincial ${normalizeWhitespace(provincialMatch[1])}`;
  }

  const genericMatch = normalized.match(/^ruta\s+(.+)$/i);
  if (genericMatch?.[1]) {
    return `Ruta ${normalizeWhitespace(genericMatch[1])}`;
  }

  return normalized;
};

const extractRouteLabel = ({ address = {}, displayName = '' }) => {
  const candidates = [
    address.road,
    address.route,
    address.path,
    address.pedestrian,
    address.footway,
    displayName ? displayName.split(',')[0] : '',
  ]
    .map((candidate) => normalizeWhitespace(candidate))
    .filter(Boolean);

  const routeCandidate = candidates.find((candidate) => ROUTE_LABEL_PATTERN.test(candidate));
  return routeCandidate ? formatRouteLabel(routeCandidate) : '';
};

const normalizeKilometerValue = (value = '') => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) {
    return '';
  }

  const kilometerMatch = normalized.match(/\bkm\.?\s*([0-9]+(?:[.,][0-9]+)?)\b/i);
  if (kilometerMatch?.[1]) {
    return kilometerMatch[1].replace('.', ',');
  }

  if (/^[0-9]+(?:[.,][0-9]+)?$/.test(normalized)) {
    return normalized.replace('.', ',');
  }

  const numberMatch = normalized.match(/([0-9]+(?:[.,][0-9]+)?)/);
  return numberMatch?.[1] ? numberMatch[1].replace('.', ',') : '';
};

const extractKilometerFromLocation = ({ address = {}, displayName = '' }) => {
  const candidates = [
    address.house_number,
    address.milestone,
    address['addr:housenumber'],
    displayName,
  ];

  for (const candidate of candidates) {
    const kilometer = normalizeKilometerValue(candidate);
    if (kilometer) {
      return kilometer;
    }
  }

  return '';
};

const toRadians = (value) => (value * Math.PI) / 180;

const calculateDistanceMeters = (latA, lngA, latB, lngB) => {
  const earthRadius = 6371000;
  const deltaLat = toRadians(latB - latA);
  const deltaLng = toRadians(lngB - lngA);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(latA)) * Math.cos(toRadians(latB)) * Math.sin(deltaLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const getOverpassElementCoordinates = (element = {}) => {
  if (Number.isFinite(element.lat) && Number.isFinite(element.lon)) {
    return {
      lat: element.lat,
      lng: element.lon,
    };
  }

  if (Number.isFinite(element.center?.lat) && Number.isFinite(element.center?.lon)) {
    return {
      lat: element.center.lat,
      lng: element.center.lon,
    };
  }

  return null;
};

const extractKilometerFromMilestoneTags = (tags = {}) => {
  const candidates = [
    tags.distance,
    tags['distance:km'],
    tags['distance:kilometre'],
    tags['distance:kilometer'],
    tags['distance:kilometres'],
    tags['distance:kilometers'],
    tags.km,
    tags.ref,
    tags.name,
    tags.description,
    tags['addr:housenumber'],
  ];

  for (const candidate of candidates) {
    const kilometer = normalizeKilometerValue(candidate);
    if (kilometer) {
      return kilometer;
    }
  }

  return '';
};

const findNearestRouteMilestone = async ({ lat, lng }) => {
  const query = `
[out:json][timeout:10];
(
  node(around:1500,${lat},${lng})["highway"="milestone"];
  way(around:1500,${lat},${lng})["highway"="milestone"];
  relation(around:1500,${lat},${lng})["highway"="milestone"];
);
out center tags;
  `.trim();

  const response = await fetch(OVERPASS_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain;charset=UTF-8',
    },
    body: query,
  });

  if (!response.ok) {
    throw new Error('No se pudo buscar el kilometro de la ruta.');
  }

  const payload = await response.json();
  if (!Array.isArray(payload.elements) || payload.elements.length === 0) {
    return null;
  }

  const nearestMilestone = payload.elements
    .map((element) => {
      const coordinates = getOverpassElementCoordinates(element);
      const kilometer = extractKilometerFromMilestoneTags(element.tags || {});
      if (!coordinates || !kilometer) {
        return null;
      }

      return {
        kilometer,
        distanceMeters: calculateDistanceMeters(lat, lng, coordinates.lat, coordinates.lng),
      };
    })
    .filter(Boolean)
    .sort((itemA, itemB) => itemA.distanceMeters - itemB.distanceMeters)[0];

  if (!nearestMilestone || nearestMilestone.distanceMeters > 2000) {
    return null;
  }

  return nearestMilestone;
};

const buildLocationSearchQuery = ({ locality, province }) => {
  const localityLabel = String(locality || '').trim();
  const provinceLabel = String(province || '').trim();

  if (!localityLabel && !provinceLabel) {
    return '';
  }

  const parts = [localityLabel, provinceLabel, 'Argentina']
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

const reverseGeocodeLocation = async ({ lat, lng }) => {
  const url = new URL(`${NOMINATIM_BASE_URL}/reverse`);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  url.searchParams.set('zoom', '18');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('accept-language', 'es');

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('No se pudo resolver la dirección.');
  }

  const payload = await response.json();
  const address = payload.address || {};
  const roadParts = [address.road, address.house_number].filter(Boolean);
  const localityLabel =
    address.city || address.town || address.village || address.hamlet || address.suburb || address.county || '';
  const provinceLabel = address.state || '';
  const routeLabel = extractRouteLabel({ address, displayName: payload.display_name || '' });
  let routeKilometer = routeLabel ? extractKilometerFromLocation({ address, displayName: payload.display_name || '' }) : '';

  if (routeLabel && !routeKilometer) {
    routeKilometer = await findNearestRouteMilestone({ lat, lng })
      .then((milestone) => milestone?.kilometer || '')
      .catch(() => '');
  }

  const routeAddressLabel = routeLabel
    ? [routeLabel, routeKilometer ? `km ${routeKilometer}` : ''].filter(Boolean).join(', ')
    : '';

  return {
    locality: localityLabel,
    province: provinceLabel,
    address: routeAddressLabel || roadParts.join(' ').trim() || payload.display_name || '',
    routeLabel,
    routeKilometer,
  };
};

const geocodeLocality = async ({ locality, province }) => {
  const query = buildLocationSearchQuery({ locality, province });
  if (!query) {
    return null;
  }

  const url = new URL(`${NOMINATIM_BASE_URL}/search`);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '1');
  url.searchParams.set('countrycodes', 'ar');
  url.searchParams.set('q', query);
  url.searchParams.set('accept-language', 'es');

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('No se pudo buscar la localidad.');
  }

  const payload = await response.json();
  if (!Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const firstMatch = payload[0];
  const lat = Number.parseFloat(firstMatch.lat);
  const lng = Number.parseFloat(firstMatch.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    lat: Number(lat.toFixed(6)),
    lng: Number(lng.toFixed(6)),
  };
};

const parseSharedMapValue = (rawValue) => {
  const value = String(rawValue || '').trim();
  if (!value) {
    return null;
  }

  const coordinatePatterns = [
    /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    /[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /[?&]ll=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/,
  ];

  for (const pattern of coordinatePatterns) {
    const match = value.match(pattern);
    if (!match) {
      continue;
    }

    const lat = Number.parseFloat(match[1]);
    const lng = Number.parseFloat(match[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return {
        lat: Number(lat.toFixed(6)),
        lng: Number(lng.toFixed(6)),
      };
    }
  }

  try {
    const parsedUrl = new URL(value);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (hostname === 'maps.app.goo.gl' || hostname === 'goo.gl') {
      return {
        unsupportedShortLink: true,
      };
    }

    const queryParamCandidates = ['q', 'query', 'destination', 'daddr'];
    for (const paramName of queryParamCandidates) {
      const rawParam = parsedUrl.searchParams.get(paramName);
      const decodedParam = decodeSearchText(rawParam);
      if (!decodedParam) {
        continue;
      }

      const latLng = decodedParam.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
      if (latLng) {
        return {
          lat: Number(Number.parseFloat(latLng[1]).toFixed(6)),
          lng: Number(Number.parseFloat(latLng[2]).toFixed(6)),
        };
      }

      return {
        query: decodedParam,
      };
    }

    const pathname = decodeSearchText(parsedUrl.pathname);
    const placeMatch = pathname.match(/\/place\/([^/]+)/i) || pathname.match(/\/search\/([^/]+)/i);
    if (placeMatch?.[1]) {
      return {
        query: placeMatch[1],
      };
    }

    return {
      unsupportedUrl: true,
    };
  } catch (error) {
    // Si no es una URL válida, seguimos tratándolo como texto libre.
  }

  return {
    query: value,
  };
};

const geocodeSearchQuery = async (query) => {
  const normalizedQuery = String(query || '').trim();
  if (!normalizedQuery) {
    return null;
  }

  const url = new URL(`${NOMINATIM_BASE_URL}/search`);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '1');
  url.searchParams.set('countrycodes', 'ar');
  url.searchParams.set('q', normalizedQuery);
  url.searchParams.set('accept-language', 'es');

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('No se pudo buscar la dirección.');
  }

  const payload = await response.json();
  if (!Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const firstMatch = payload[0];
  const lat = Number.parseFloat(firstMatch.lat);
  const lng = Number.parseFloat(firstMatch.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    lat: Number(lat.toFixed(6)),
    lng: Number(lng.toFixed(6)),
  };
};

const resolveGoogleMapsShortLink = async (rawUrl) => {
  const response = await fetch(
    apiUrl(`/api/resolve-shared-map-link/?url=${encodeURIComponent(String(rawUrl || '').trim())}`)
  );

  if (!response.ok) {
    let detail = 'No se pudo resolver el enlace corto de Google Maps.';
    try {
      const payload = await response.json();
      if (payload?.detail) {
        detail = payload.detail;
      }
    } catch (error) {
      // Si falla el parseo del error, usamos el mensaje genÃ©rico.
    }
    throw new Error(detail);
  }

  const payload = await response.json();
  return String(payload?.resolved_url || '').trim();
};

const CaseLocationPicker = ({ latitude, longitude, locality, province, onChange }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const defaultMarkerRef = useRef(null);
  const lastResolvedSearchRef = useRef('');
  const [mapError, setMapError] = useState('');
  const [mapNotice, setMapNotice] = useState('');
  const [sharedLocationInput, setSharedLocationInput] = useState('');

  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) {
      return undefined;
    }

    if (!window.L) {
      setMapError('No se pudo cargar el mapa.');
      return undefined;
    }

    const initialLat = parseCoordinate(latitude);
    const initialLng = parseCoordinate(longitude);
    const hasInitialPoint = initialLat !== null && initialLng !== null;

    const map = window.L.map(mapContainerRef.current, {
      center: hasInitialPoint ? [initialLat, initialLng] : DEFAULT_CENTER,
      zoom: hasInitialPoint ? DETAIL_ZOOM : DEFAULT_ZOOM,
      scrollWheelZoom: true,
    });

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    addLeafletCompassControl(window.L, map, {
      title: 'Brújula: el mapa mantiene el norte hacia arriba.',
    });

    if (!hasInitialPoint) {
      defaultMarkerRef.current = window.L.circleMarker(DEFAULT_CENTER, {
        radius: 8,
        color: '#0c5ea8',
        weight: 2,
        fillColor: '#6fd3ff',
        fillOpacity: 0.85,
      })
        .addTo(map)
        .bindPopup(INTA_CORDOBA_LABEL);
      setMapNotice(`Mapa centrado en ${INTA_CORDOBA_LABEL}.`);
    }

    map.on('click', async (event) => {
      const { lat, lng } = event.latlng;
      const nextLat = Number(lat.toFixed(6));
      const nextLng = Number(lng.toFixed(6));

      setMapError('');
      setMapNotice('Buscando dirección aproximada...');

      try {
        const resolvedLocation = await reverseGeocodeLocation({ lat: nextLat, lng: nextLng });
        lastResolvedSearchRef.current = buildLocationSearchQuery({
          locality: resolvedLocation?.locality,
          province: resolvedLocation?.province,
        });
        onChange({
          lat: nextLat,
          lng: nextLng,
          resolvedLocation,
        });
        setMapNotice(
          resolvedLocation?.routeKilometer
            ? 'Se encontró una referencia rural con ruta y km. Revísala antes de usarla.'
            : resolvedLocation?.routeLabel
              ? 'Se encontró una referencia sobre la ruta. Revísala antes de usarla.'
              : 'Se encontró una dirección sugerida a partir del punto. Revísala antes de usarla.'
        );
      } catch (error) {
        onChange({
          lat: nextLat,
          lng: nextLng,
        });
        setMapNotice('Se guardó el punto, pero no se pudo estimar la dirección.');
      }
    });

    mapInstanceRef.current = map;

    window.setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
      defaultMarkerRef.current = null;
    };
  }, [latitude, longitude, onChange]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.L) {
      return;
    }

    const lat = parseCoordinate(latitude);
    const lng = parseCoordinate(longitude);

    if (lat === null || lng === null) {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      if (!defaultMarkerRef.current) {
        defaultMarkerRef.current = window.L.circleMarker(DEFAULT_CENTER, {
          radius: 8,
          color: '#0c5ea8',
          weight: 2,
          fillColor: '#6fd3ff',
          fillOpacity: 0.85,
        })
          .addTo(map)
          .bindPopup(INTA_CORDOBA_LABEL);
      }
      setMapNotice(`Mapa centrado en ${INTA_CORDOBA_LABEL}.`);
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    if (defaultMarkerRef.current) {
      map.removeLayer(defaultMarkerRef.current);
      defaultMarkerRef.current = null;
    }

    if (!markerRef.current) {
      markerRef.current = window.L.marker([lat, lng]).addTo(map);
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }

    map.setView([lat, lng], DETAIL_ZOOM);
  }, [latitude, longitude]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const searchQuery = buildLocationSearchQuery({ locality, province });

    if (!map || !searchQuery || searchQuery === lastResolvedSearchRef.current) {
      return undefined;
    }

    const timerId = window.setTimeout(async () => {
      setMapError('');
      setMapNotice(`Buscando ${searchQuery} en el mapa...`);

      try {
        const coordinates = await geocodeLocality({ locality, province });
        if (!coordinates) {
          setMapNotice(`No encontramos ${searchQuery} con suficiente precisión.`);
          return;
        }

        lastResolvedSearchRef.current = searchQuery;
        onChange(coordinates);
        map.setView([coordinates.lat, coordinates.lng], SEARCH_ZOOM);
        setMapNotice(`Mapa centrado en ${searchQuery}.`);
      } catch (error) {
        setMapError('No se pudo ubicar la localidad en el mapa.');
        setMapNotice('');
      }
    }, 700);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [locality, province, onChange]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError('Tu navegador no permite obtener la ubicación actual.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const nextLat = Number(coords.latitude.toFixed(6));
        const nextLng = Number(coords.longitude.toFixed(6));

        setMapError('');
        setMapNotice('Buscando dirección aproximada...');

        try {
          const resolvedLocation = await reverseGeocodeLocation({ lat: nextLat, lng: nextLng });
          lastResolvedSearchRef.current = buildLocationSearchQuery({
            locality: resolvedLocation?.locality,
            province: resolvedLocation?.province,
          });
          onChange({
            lat: nextLat,
            lng: nextLng,
            resolvedLocation,
          });
          setMapNotice(
            resolvedLocation?.routeKilometer
              ? 'Se encontró una referencia rural con ruta y km. Revísala antes de usarla.'
              : resolvedLocation?.routeLabel
                ? 'Se encontró una referencia sobre la ruta. Revísala antes de usarla.'
                : 'Se encontró una dirección sugerida desde tu ubicación actual. Revísala antes de usarla.'
          );
        } catch (error) {
          onChange({
            lat: nextLat,
            lng: nextLng,
          });
          setMapNotice('Se obtuvo la ubicación actual, pero no la dirección estimada.');
        }
      },
      () => {
        setMapError('No se pudo obtener la ubicación actual.');
      }
    );
  };

  const handleSharedLocationSearch = async () => {
    let parsedValue = parseSharedMapValue(sharedLocationInput);
    if (!parsedValue) {
      setMapError('Pega una dirección, coordenadas o un enlace compartido de Google Maps.');
      setMapNotice('');
      return;
    }

    if (parsedValue.unsupportedShortLink) {
      setMapError('');
      setMapNotice('Resolviendo enlace corto de Google Maps...');

      try {
        const resolvedUrl = await resolveGoogleMapsShortLink(sharedLocationInput);
        parsedValue = parseSharedMapValue(resolvedUrl);
      } catch (error) {
        setMapNotice('');
        setMapError(error.message || 'No se pudo resolver el enlace corto de Google Maps.');
        return;
      }

      if (!parsedValue || parsedValue.unsupportedShortLink || parsedValue.unsupportedUrl) {
        setMapNotice('');
        setMapError(
          'Se resolvió el enlace, pero no se pudo extraer una ubicación útil. Prueba con la dirección escrita o las coordenadas.'
        );
        return;
      }
    }

    if (parsedValue.unsupportedUrl) {
      setMapNotice('');
      setMapError(
        'No se pudo extraer una ubicación útil desde ese enlace. Prueba con la dirección escrita, las coordenadas o un enlace completo que incluya el lugar.'
      );
      return;
    }

    setMapError('');
    setMapNotice('Interpretando ubicación compartida...');

    try {
      const coordinates = parsedValue.query
        ? await geocodeSearchQuery(parsedValue.query)
        : parsedValue;

      if (!coordinates) {
        setMapNotice('');
        setMapError('No se pudo ubicar ese enlace o dirección en el mapa.');
        return;
      }

      const resolvedLocation = await reverseGeocodeLocation(coordinates).catch(() => null);
      if (resolvedLocation) {
        lastResolvedSearchRef.current = buildLocationSearchQuery({
          locality: resolvedLocation?.locality,
          province: resolvedLocation?.province,
        });
      }
      onChange({
        lat: coordinates.lat,
        lng: coordinates.lng,
        resolvedLocation: resolvedLocation || undefined,
      });
      setMapNotice(
        resolvedLocation?.routeKilometer
          ? 'Ubicación cargada en el mapa con referencia de ruta y km. Puedes aceptarla si corresponde.'
          : resolvedLocation?.routeLabel
            ? 'Ubicación cargada en el mapa con referencia sobre la ruta. Puedes aceptarla si corresponde.'
            : 'Ubicación cargada en el mapa. Si corresponde, puedes aceptar la dirección sugerida.'
      );
    } catch (error) {
      setMapNotice('');
      setMapError('No se pudo interpretar la ubicación compartida.');
    }
  };

  const handleSharedLocationKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleSharedLocationSearch();
  };

  return (
    <div className="case-map-card">
      <div className="case-map-card__header">
        <div>
          <label className="form-label mb-1">Ubicación del caso</label>
          <p className="case-map-card__hint mb-0">
            Hace clic en el mapa para marcar un punto exacto, usa tu ubicación actual o escribe una localidad para centrar el mapa. El mapa inicia en {INTA_CORDOBA_LABEL} y, si encuentra una dirección, quedará como sugerencia para que la confirmes.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-brand btn-sm"
          onClick={handleUseCurrentLocation}
        >
          Usar mi ubicación actual
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label">Pegar dirección o enlace compartido</label>
        <div className="d-flex flex-column flex-md-row gap-2">
          <input
            type="text"
            className="form-control"
            value={sharedLocationInput}
            onChange={(event) => setSharedLocationInput(event.target.value)}
            onKeyDown={handleSharedLocationKeyDown}
            placeholder="Ej. Av. Siempre Viva 742, Salta o enlace de Google Maps"
          />
          <button type="button" className="btn btn-outline-brand" onClick={handleSharedLocationSearch}>
            Buscar en mapa
          </button>
        </div>
        <div className="form-text mt-2">
          Puedes pegar una dirección, coordenadas o un enlace copiado desde Google Maps.
        </div>
      </div>

      <div ref={mapContainerRef} className="case-map" />

      <div className="case-map-card__footer">
        <span className="case-map-card__coords">
          {latitude && longitude
            ? `Punto seleccionado: ${latitude}, ${longitude}`
            : 'Todavía no hay un punto seleccionado.'}
        </span>
        {(latitude || longitude) && (
          <button
            type="button"
            className="btn btn-link case-map-card__clear"
            onClick={() => onChange({ lat: '', lng: '' })}
          >
            Limpiar punto
          </button>
        )}
      </div>

      {mapNotice ? <div className="form-text mt-2">{mapNotice}</div> : null}
      {mapError && <div className="alert subtle-alert mt-3 mb-0">{mapError}</div>}
    </div>
  );
};

export default CaseLocationPicker;
